// MapMenuAndWatch.js
import React, {useEffect, useState} from 'react';
import { styled  } from '@mui/material/styles';
import { useInterval } from 'react-use';
import { Box } from '@mui/system';
import {
    Button, ButtonGroup, Popover, Slider, IconButton,
    MenuList, MenuItem, ListItemIcon, ListItemText,
    CircularProgress, FormControlLabel, Checkbox
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ModeOfTravelIcon from '@mui/icons-material/ModeOfTravel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PowerIcon from '@mui/icons-material/Power';
import DomainIcon from '@mui/icons-material/Domain';
import { PosTailHourLimit } from './PositionTail';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import {
    ApiMsg, AppObject, AppStyle, AppWord, MapEvent, MapValue
} from '../../value/AppConst';
import { LIST_MAP_POI_BY_DC, MapPoiAllDictionary } from '../map_poi_all/MapPoiAllDict';
import ValueMisc from '../../value/ValueMisc';
import { mapPoiAllListRepo, rtdTermPosListRepo, userInfoRepo } from '../../value/AppModel';
import { useAlertSetter } from '../../api/app-hook';
import UserSessionInfo from '../../value/UserSessionInfo';
import { useSnackbar } from 'notistack';
import { SET_MAP_VIEW_ZERO_USER_INFO } from '../user_info/UserInfoGql';
import {
    LIST_RTD_TERM_POS_FOR_MAP, LIST_RTD_TERM_POS_FOR_STORE
} from '../rtd_term_pos/RtdTermPosGql';

const MinimalContainer = styled(Box)({display:'inline',marginLeft:10});

const PopControlBox = styled(Box)({
    minWidth:200, padding:10, borderRadius: 5,
    backgroundColor: '#f6f9ff', border: "1px solid "+AppStyle.PrimaryRGB,
    display:'flex', flexDirection:'column'
});

const SectionRow = styled(Box)({
    padding:'5px 0px 10px 0px', display:'flex', borderTop: '1px solid #cdf'
});

const InfoRow = styled(Box)({
    display:'flex', padding:'5px 0px', alignItems: 'center', fontSize:'0.85rem'
})
const MapScaleBox = styled(Box)({ minWidth:180, margin:10 });
const PopTopRight = { vertical: 'top', horizontal: 'right', };
const MapTypeBox = styled(Box)({ paddingLeft: 10 });
const LabelBox = styled('div')({fontSize:'0.85rem', color:AppStyle.PrimaryRGB, padding:"10px 2px 5px 5px"});

const FetchInterval = 61; // seconds

function readLocalStorage() {
    const text = localStorage.getItem(AppWord.MAP_LOCALSTORAGE_KEY);
    if(text) {
        const conf = JSON.parse(text);
        if(conf) {
            return conf;
        }
    }
    return {};
}

function saveLocalStorage(conf) {
    const text = JSON.stringify(conf);
    localStorage.setItem(AppWord.MAP_LOCALSTORAGE_KEY, text);
}

/**
 * 0.Menu, 지도 스케일, 종류 선택을 위한 Popover
 * 1.차량정보 자동 수신, POI 조회 등을 위한 ...
 * @param {object} param0 {mapShell, onAlert}
 * @returns 
 */
export default function MapMenuAndWatch({
    mapShell,
    disabled,
}) {
	const sessionInfo = useReactiveVar(userInfoRepo);
    const [anchorElement, setAnchorElement] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(MapValue.Level.ZOOM_INIT);
    const [posTailHourLimit, setPosTailHourLimit] = useState(2);
    const [mapType, setMapType] = useState(window.naver.maps.MapTypeId.NORMAL);
    const rtdTermPosListData = useReactiveVar(rtdTermPosListRepo);
    const [retrieveVehPosNow, setRetrieveVehPosNow] = useState(false);
    const [timeTryAndGet, setTimeTryAndGet] = useState(0); // Date().getTime() - 차량위치 가져온 시각.
    const [secNow, setSecNow] = useState(0);
    const [useCluster, setUseCluster] = useState(mapShell ? mapShell.useCluster : false); // 클러스터 사용 여부. 2026.6.22
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    
    //const isAdmin = sessionInfo.isEtrace();

    // ##### Call GraphQL to get List #####
    const [getMapPoiListByDc, responsePoiList]
    = useLazyQuery(LIST_MAP_POI_BY_DC, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetPoiListByDc(data, option)},
		onError: (error) => addAlert({title: MapPoiAllDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getRtdTermPosList, responseRtdTermPosList] = useLazyQuery(LIST_RTD_TERM_POS_FOR_MAP, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetPosList(data, option)},
		onError: (error) => addAlert({title: '위치목록조회 오류', message: [error.message]})
    });
    // 점포용 목록: 관제리스트 조회와 거의 같은 조건의 조회.
    const [getMapPoiListByDcForStore, responsePoiListForStore] = useLazyQuery(LIST_RTD_TERM_POS_FOR_STORE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetPosListForStore(data, option)},
        onError: (error) => addAlert({title: '위치목록조회 오류', message: [error.message]})
    })

    const [setUserInfoMapViewZero, responseSetMapViewZero] = useMutation(SET_MAP_VIEW_ZERO_USER_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => onCompleteSetMapViewZero(data, option),
        onError: (error) => addAlert({title: "지도 초기화면 설정 오류", message: [error.message]})
    });

    /* useEffect(()=>{
        getPoiAndBcdList();
    }, []); */

    useEffect(()=>{
        if(mapShell) {
            if(!sessionInfo.isStoreUser() && !sessionInfo.isDriver())
                getMapPoiListByDc({variables:{dcCd:sessionInfo.user.getDcCd()}});

            setZoomLevel(mapShell.getZoom());
            setMapType(mapShell.getMapType());
            mapShell.setMapEventHandler(MapEvent.ZOOM_CHANGED, MapEvent.ZOOM_CHANGED+'map_scale_and_watch', (level) => {
                setZoomLevel(level);
            });

            let hourLimit;
            const conf = readLocalStorage();

            if(conf.posTrackTailLimit) {
                mapShell.setVehPosTrackLimit(conf.posTrackTailLimit);
            }

            hourLimit = mapShell.getVehPosTrackLimit();
            setPosTailHourLimit(hourLimit);
        }
    }, [mapShell, sessionInfo]);

    // 위치조회 반복 실행.
    const ticker = () => {
        const now = new Date().getTime() / 1000;
        const diff = now - rtdTermPosListData.when; // 실제 가저온 시각과의 차이.
        const diffTry = now - timeTryAndGet;
        setSecNow(now);
        // 실제가져온지 60초 지났거나, 실패로 인해 재시도한지 5초가 지났을 때.
        if((diff > FetchInterval && diffTry > 5) || retrieveVehPosNow) {
            setRetrieveVehPosNow(false);
            setTimeTryAndGet(now); // 시도한 시각을 기록.
            // 점포사용자인 경우 점포용 목록 가져오기.
            if(sessionInfo.isStoreUser()) getMapPoiListByDcForStore();
            else getRtdTermPosList();
        }
    };

    useInterval(ticker, 1000);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetPoiListByDc = (data, clientOption) => {
        if(data.mapPoiListByDc) {
            mapPoiAllListRepo({
                when: new Date().getTime() / 1000,
                records: data.mapPoiListByDc.map((p)=>{
                    if((!p.lat || !p.lon) && p.xCoord && p.yCoord) {
                        const {lat, lon} = mapShell.katek2ll({x:p.xCoord, y:p.yCoord});
                        p.lat = lat;
                        p.lon = lon;
                        return p;
                    }
                    return p;
                })
            });
        }
    };

    const checkSessionDataOnGetPosList = (info) => {
        if(info.sessionNote===ApiMsg.OK) {
            if(info.dscIdAs !== sessionInfo.user.dscIdAs) {
                const newInfo = UserSessionInfo.initFromUserObject(info);
                userInfoRepo(newInfo);
                // get poi again by useEffect above.
            }
        }
    };

    // 데이터 수신한 후 repo에 저장하면 MapMain 컴포넌트에서 필터링하여 또 다른 repo에 저장.
    // 필터링 기준 텍스트 역시 repo에 있음.
    // 이렇게 복잡하게 하는 이유는 
    // 1. 위치데이터가 자동으로 수신됨.
    // 2. 위치데이터가 필터링된 후 지도와 테이블에 동시 반영됨.
    // 3. 필터링 텍스트 입력은 테이블이 있는 곳에서 이루어짐.
    const onCompleteGetPosList = (data, clientOption) => {
        if(data.rtdTermPosListForMap) {
            rtdTermPosListRepo({when:new Date().getTime()/1000, records:data.rtdTermPosListForMap});
        }
        if(data.getSessionData) {
            checkSessionDataOnGetPosList(data.getSessionData);
        }
    };

    const onCompleteGetPosListForStore = (data, clientOption) => {
        if(data.rtdTermPosListForStore) {
            rtdTermPosListRepo({when:new Date().getTime()/1000, records:data.rtdTermPosListForStore});
        }
        if(data.getSessionData) {
            checkSessionDataOnGetPosList(data.getSessionData);
        }
    };

    const onCompleteSetMapViewZero = (data, clientOption) => {
        if(data.userInfoSetMapViewZero.ok) {
            enqueueSnackbar('지도 초기화면을 현재 위치와 줌레벨 상태로 설정했습니다.', {variant:'success', autoHideDuration:2000});
        }
        else addAlert({title:'지도 초기상태 설정 오류', message:[data.userInfoSetMapViewZero.message]});
    };

    // 차량관제쪽에서는 운전자의 수동보고에 대해 스낵을 띄울 수 있었음. 뉴탑에서는 나중에 다시 생각하기로...
    /* const onCompleteGetVehPosList = (data, clientOption) => {
        // find current itemSelected and reset.
        const now = new Date().getTime() / 1000;
        if(data.vehPosList) {
            vehPosRepo({when:now, records: data.vehPosList});
            // rec.man_report_yn=='Y' && rec.ir_action != 0 && rec.ir_action != 255
            data.vehPosList
                .filter((vp)=>vp.manReportYn==='Y' && vp.irAction !==0 && vp.irAction !== 255 && vp.reportAgeSec < 600 && vp.irActionName)
                .forEach(p => {
                    Util.bubbleSnack(`${p.vehPlates} ${p.gpsTime}: ${p.irActionName} (${p.location})`);
                });
        }
    }; */

    const onChangeLevel = (level) => {
        // setZoomLevel(level);
        mapShell.setZoom(level);
    };

    const onChangeMapType = (type) => {
        mapShell.setMapType(type);
        setMapType(type);
    };

    const onChangePosTailHourLimit = (hours) => {
        setPosTailHourLimit(hours);
        mapShell.setVehPosTrackLimit(hours);

        const conf = readLocalStorage();
        conf.posTrackTailLimit = hours;
        saveLocalStorage(conf);
    };

    const onClickSetInitView = () => {
        setAnchorElement(null);
        const level = mapShell.getZoom();
        const pos = mapShell.getCenter(); // {lat, lon}

        setUserInfoMapViewZero({
            variables: {
                userInfo: {
                    viewLevel: level,
                    initLat: pos.lat,
                    initLon: pos.lon
                }
            }
        });
    };

    const onClickReloadVehPos = () => {
        setRetrieveVehPosNow(true);
        setAnchorElement(null);
    };

    const popMapWindow = (param) => {
        const width = window.innerWidth; // > 2000 ? 2000 : window.screen.width - 50;
        const height = window.innerHeight; // > 2000 ? 2000 : window.screen.height - 50;
        let url = ValueMisc.getUrlBase() + "#" + AppWord.MAP_SUB_URL;
        if(param) {
            for(const pkey of Object.keys(param)) {
                const val = param[pkey];
                url += `&${pkey}=${val}`;
            }
        }
        window.open(url, '_blank', MapValue.PopupOption + `,width=${width},height=${height}`);
    };

    const onClickPopAnotherWindow = () => {
        popMapWindow();
        setAnchorElement(null);
    };

    const onChangeUseCluster = (event) => {
        setUseCluster(event.target.checked);
        mapShell.setUseCluster(event.target.checked);
        mapShell.showPoiMarkers();
    };

    const marks = MapValue.LevelOptions.map(option => ({
        value: option.level,
        label: option.label,
    }));

    const popId = 'map-scale-and-watcher-pop';
    const openPopOver = Boolean(anchorElement);

    const loadingAny
    = responsePoiList.loading
    || responseRtdTermPosList.loading
    || responsePoiListForStore.loading;

    return (
        <MinimalContainer>
            {
                responsePoiList.loading ?
                <CircularProgress size={20} />
                :
                <IconButton size={AppWord.SMALL} color={AppStyle.mui.inherit}
                    onMouseEnter={(e)=>{if(!disabled) setAnchorElement(e.currentTarget)}}
                >
                    <MenuIcon fontSize={AppWord.SMALL} />
                </IconButton>
            }
            <Popover
                id={popId}
                open={openPopOver}
                onClose={()=>setAnchorElement(null)}
                anchorEl={anchorElement}
                anchorOrigin={PopTopRight}
                transformOrigin={PopTopRight}
            >
                <PopControlBox onMouseLeave={()=>setAnchorElement(null)}>
                    <SectionRow style={{borderTop:'0px', flexDirection:'column'}}>
                        <InfoRow>
                            <PersonOutlineIcon style={{marginRight:10}} />
                            {sessionInfo.user.userName} 님
                        </InfoRow>
                        <InfoRow>
                            <DomainIcon style={{marginRight:10}} />
                            {sessionInfo.user.dscNameAs}
                        </InfoRow>
                    </SectionRow>
                    <SectionRow>
                        <MapScaleBox>
                            <Slider
                                value={zoomLevel}
                                orientation="vertical"
                                onChange={(e,v)=>onChangeLevel(v)}
                                style={{height:360}}
                                min={MapValue.Level.ZOOM_MIN}
                                max={MapValue.Level.ZOOM_MAX}
                                step={1}
                                valueLabelDisplay="off"
                                aria-labelledby="level-slider"
                                marks={marks}
                            />
                        </MapScaleBox>
                        <MapTypeBox>
                            <ButtonGroup
                                orientation='vertical'
                                aria-label="vertical outlined button group"
                            >
                                {
                                    MapValue.NaverMapTyles.map((type)=>{
                                        return(
                                            <Button
                                                key={type.type}
                                                disabled={mapType===type.type}
                                                onClick={()=>onChangeMapType(type.type)}
                                            >
                                                {type.title}
                                            </Button>
                                        );
                                    })
                                }
                            </ButtonGroup>
                        </MapTypeBox>

                    </SectionRow>
                    <SectionRow>
                        <Box style={{paddingLeft:30}}>
                            <LabelBox>차량위치 경로꼬리 제한: {posTailHourLimit}시간</LabelBox>
                            <Slider
                                value={posTailHourLimit}
                                onChange={(e,v)=>onChangePosTailHourLimit(v)}
                                min={PosTailHourLimit.Min} max={PosTailHourLimit.Max}
                                step={1}
                                valueLabelDisplay='auto'
                                getAriaValueText={(v)=>`${v}시간`}
                                aria-labelledby="hour-limit-slider"
                                style={{width:'90%'}}
                            />
                            {/*
                            Using    valueLabelFormat={(v) => `${v} 시간`}
                            =>
                            "hook.js:608 Warning: Failed prop type: Invalid prop `value` of type `string` supplied to `MuiSliderValueLabel`, expected `number`"
                            Resolve by replacing valueLabelFormat with getAriaValueText
                            */}
                        </Box>
                    </SectionRow>
                    <SectionRow>
                        <FormControlLabel
                            label="지점 그룹화 사용"
                            control={
                                <Checkbox
                                    name="cluster-check"
                                    checked={useCluster}
                                    onChange={onChangeUseCluster}
                                />
                            }
                        />
                    </SectionRow>
                    <SectionRow>
                        <MenuList>
                            <MenuItem
                                onClick={onClickSetInitView}
                            >
                                <ListItemIcon>
                                    <PowerIcon fontSize={AppWord.SMALL}/>
                                </ListItemIcon>
                                <ListItemText>지도 초기화면을 지금 상태로 설정</ListItemText>
                            </MenuItem>
                            <MenuItem disabled={
                                    (secNow - rtdTermPosListData.when) > (FetchInterval - 7)
                                    || (secNow - rtdTermPosListData.when) < 7
                                    || loadingAny
                                }
                                onClick={onClickReloadVehPos}
                            >
                                <ListItemIcon>
                                    <ModeOfTravelIcon fontSize={AppWord.SMALL}/>
                                </ListItemIcon>
                                <ListItemText>차량위치 다시 읽기 ({FetchInterval - Math.round(secNow - rtdTermPosListData.when)})</ListItemText>
                            </MenuItem>
                            <MenuItem
                                onClick={onClickPopAnotherWindow}
                            >
                                <ListItemIcon>
                                    <OpenInNewIcon fontSize={AppWord.SMALL}/>
                                </ListItemIcon>
                                <ListItemText>새로운 지도 창 띄우기</ListItemText>
                            </MenuItem>
                        </MenuList>

                    </SectionRow>

                </PopControlBox>
            </Popover>
        </MinimalContainer>
    )
}
